import React, { useEffect, useState } from 'react';

import { IChangeEvent } from '@rjsf/core';
import Form from '@rjsf/mui';
import { CustomValidator, RJSFSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';

import { Alert, Box, Button, CircularProgress, Link, Typography } from '@mui/material';

import { useHotelBookingsReportJob, useQueueHotelBookingsReport } from '~/hooks/apis/reporting/useHotelBookingsReport';

import { addMonths, formatDateISO } from '~/services/TimeService';

import ErrorDisplay from '../ErrorDisplay';

const validateForm: CustomValidator<HotelBookingsFormData> = (formData, errors) => {
  if (formData && !formData.startAt) {
    errors.startAt.addError('Please enter a start date');
  }
  if (formData && !formData.endAt) {
    errors.endAt.addError('Please enter an end date');
  }
  if (
    formData &&
    formData.purchasedFrom &&
    formData.purchasedBefore &&
    formData.purchasedFrom >= formData.purchasedBefore
  ) {
    errors.purchasedBefore.addError('Purchased before must be after purchased from');
  }
  if (formData && !formData.propertyName && !formData.propertyId && !formData.vendorId && !formData.offerId) {
    errors.propertyName.addError('Enter a hotel name, property ID, vendor ID, or offer ID');
  }

  return errors;
};

const HOW_IT_WORKS = [
  'Returns every LTLE, LPC and Bedbank booking for one hotel, with customer name and email.',
  'Identify the hotel by name (e.g. Sage Hotel Wollongong), Property ID or Vendor ID. Name is a partial match on the property or vendor name, so a short name can match more than one hotel.',
  'Offer ID returns that offer only. Filling in more than one identifier widens the result rather than narrowing it.',
  'Check-in from is inclusive, Check-in before is exclusive. Purchase dates are optional and use the same bounds against order date.',
  'Cancelled bookings and undated Buy Now Book Later bookings are left out unless you tick them on.',
];

const formSchema: RJSFSchema = {
  type: 'object',
  properties: {
    startAt: {
      title: 'Check-in from',
      type: 'string',
      format: 'date',
    },
    endAt: {
      title: 'Check-in before',
      type: 'string',
      format: 'date',
    },
    purchasedFrom: {
      title: 'Purchased from (optional)',
      type: 'string',
      format: 'date',
    },
    purchasedBefore: {
      title: 'Purchased before (optional)',
      type: 'string',
      format: 'date',
    },
    propertyName: {
      title: 'Hotel name',
      type: 'string',
    },
    propertyId: {
      title: 'Property ID',
      type: 'string',
    },
    vendorId: {
      title: 'Vendor ID',
      type: 'string',
    },
    offerId: {
      title: 'Offer ID or LE Offer ID',
      type: 'string',
    },
    includeCancelled: {
      title: 'Include cancelled bookings',
      type: 'boolean',
      default: false,
    },
    includeUndatedBnbl: {
      title: 'Include Buy Now Book Later bookings with no check-in/out dates selected yet',
      type: 'boolean',
      default: false,
    },
  },
  required: ['startAt', 'endAt'],
};

interface HotelBookingsFormData {
  startAt?: string;
  endAt?: string;
  purchasedFrom?: string;
  purchasedBefore?: string;
  propertyName?: string;
  propertyId?: string;
  vendorId?: string;
  offerId?: string;
  includeCancelled?: boolean;
  includeUndatedBnbl?: boolean;
}

const defaultFormData: HotelBookingsFormData = {
  startAt: formatDateISO(),
  endAt: formatDateISO(addMonths(3)),
  includeCancelled: false,
  includeUndatedBnbl: false,
};

const uiSchema = {
  startAt: {
    'ui:widget': 'date',
  },
  endAt: {
    'ui:widget': 'date',
  },
  purchasedFrom: {
    'ui:widget': 'date',
  },
  purchasedBefore: {
    'ui:widget': 'date',
  },
};

// svc-reporting fails the job with this message when the query matches no rows, which is
// what a mistyped identifier looks like. It is a miss, not a fault, so it reads differently.
const NO_BOOKINGS_PREFIX = 'No bookings found';

const IDENTIFIER_LABELS: Array<[keyof HotelBookingsFormData, string]> = [
  ['propertyName', 'Hotel name'],
  ['propertyId', 'Property ID'],
  ['vendorId', 'Vendor ID'],
  ['offerId', 'Offer ID'],
];

function describeIdentifiers(formData: HotelBookingsFormData): string {
  return IDENTIFIER_LABELS.filter(([key]) => formData[key])
    .map(([key, label]) => `${label} "${String(formData[key]).trim()}"`)
    .join(', ');
}

export default function ExportHotelBookingsForm() {
  const [formData, setFormData] = useState<HotelBookingsFormData>(defaultFormData);
  const [successMessage, setSuccessMessage] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const queueReport = useQueueHotelBookingsReport();
  const jobStatus = useHotelBookingsReportJob(jobId);
  const isRunning = Boolean(jobId) || queueReport.isPending;
  const hasResults = Boolean(jobStatus.data?.hasResults);
  const isNoBookingsFound = Boolean(error?.startsWith(NO_BOOKINGS_PREFIX));
  const searchedFor = describeIdentifiers(formData);

  useEffect(() => {
    if (!jobStatus.data) {
      return;
    }

    if (jobStatus.data.downloadUrl) {
      setDownloadUrl(jobStatus.data.downloadUrl);
    }

    if (jobStatus.data.status === 'completed') {
      setSuccessMessage(true);
      setJobId(null);
      return;
    }

    if (jobStatus.data.status === 'failed') {
      setError(jobStatus.data.error || 'Hotel bookings report failed');
      setJobId(null);
    }
  }, [jobStatus.data]);

  useEffect(() => {
    if (jobStatus.error) {
      setError(jobStatus.error instanceof Error ? jobStatus.error.message : 'Failed to check report status');
      setJobId(null);
    }
  }, [jobStatus.error]);

  const handleSubmit = async (event: IChangeEvent<HotelBookingsFormData>) => {
    if (isRunning) {
      return;
    }

    const submitted = event.formData ?? formData;
    if (!submitted.startAt || !submitted.endAt) {
      return;
    }

    setFormData(submitted);
    setError(null);
    setSuccessMessage(false);
    setDownloadUrl(null);
    try {
      const result = await queueReport.mutateAsync({
        startAt: submitted.startAt,
        endAt: submitted.endAt,
        purchasedFrom: submitted.purchasedFrom || undefined,
        purchasedBefore: submitted.purchasedBefore || undefined,
        propertyName: submitted.propertyName,
        propertyId: submitted.propertyId,
        vendorId: submitted.vendorId,
        offerId: submitted.offerId,
        includeCancelled: submitted.includeCancelled,
        includeUndatedBnbl: submitted.includeUndatedBnbl,
      });
      setJobId(result.jobId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to queue the hotel bookings report');
    }
  };

  return (
    <>
      <Box component="ul" sx={{ pl: 3, mt: 0, mb: 3, color: 'text.secondary' }}>
        {HOW_IT_WORKS.map((point) => (
          <Typography component="li" variant="body2" key={point} sx={{ mb: 0.5 }}>
            {point}
          </Typography>
        ))}
      </Box>

      <Form
        schema={formSchema}
        uiSchema={uiSchema}
        formData={formData}
        onChange={(event) => setFormData(event.formData ?? formData)}
        onSubmit={handleSubmit}
        customValidate={validateForm}
        validator={validator}
        showErrorList={false}
        disabled={isRunning}
      >
        <Button
          type="submit"
          variant="contained"
          disabled={isRunning}
          startIcon={isRunning ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isRunning
            ? hasResults
              ? 'Generating spreadsheet...'
              : 'Looking up bookings...'
            : 'Email Hotel Bookings Report'}
        </Button>

        {isRunning && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {hasResults
              ? 'Report generating and will be emailed when complete. Please wait to download here.'
              : 'Looking up bookings…'}
          </Alert>
        )}

        {isNoBookingsFound && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <p>{error}</p>
            {searchedFor && <p>Searched for {searchedFor}.</p>}
            <p>
              A mistyped or unknown ID matches nothing rather than erroring, so check the ID against Admin before
              widening the check-in range.
            </p>
          </Alert>
        )}

        {error && !isNoBookingsFound && <ErrorDisplay message={error} />}

        {error && downloadUrl && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <p>The report is ready, but the email could not be sent.</p>
            <p>
              <Link href={downloadUrl} target="_blank" rel="noopener noreferrer">
                Download now
              </Link>
            </p>
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success">
            <p>Your report has been emailed.</p>
            {downloadUrl && (
              <p>
                <Link href={downloadUrl} target="_blank" rel="noopener noreferrer">
                  Download now
                </Link>
              </p>
            )}
          </Alert>
        )}
      </Form>
    </>
  );
}
